import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { evoPathwayIcon } from "@/icons";
import { cn } from "@/lib/utils";
import { BREAKPOINTS } from "@/settings";
import {
  evolutionLevels,
  isPathwayEvolution,
  isRolePlusUpgrade,
  levelUpgradeKeys,
  pathwayPathLabel,
  rolePlusMark,
  withPathwayOption,
} from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import type { Evolution, Upgrade } from "@repo/shared/types";
import { ReactNode } from "react";
import { useMediaQuery } from "usehooks-ts";

import { LevelCard, LevelCardRow } from "./LevelCard";
import { PlayStyleUpgradeIcon, UpgradeChip } from "./UpgradeChip";

/**
 * How one upgrade shows on a level card: a label/value row for a stat, an
 * icon for a PlayStyle, a chip for a granted role. The card groups the
 * icons and chips into their own rows under the stats.
 */
export type LevelUpgradeView =
  | { kind?: "row"; label: ReactNode; value: ReactNode }
  | { kind: "icon"; node: ReactNode }
  | { kind: "chip"; node: ReactNode };

export type UpgradeFormatter = (upgrade: Upgrade) => LevelUpgradeView;

/**
 * The view an upgrade gets when the page passes no formatter: PlayStyles
 * and roles as art, everything else as the backend's English row.
 */
export const defaultUpgradeView = (upgrade: Upgrade): LevelUpgradeView => {
  if (upgrade.type === "playstyle") {
    return {
      kind: "icon",
      node: (
        <PlayStyleUpgradeIcon
          playStyleId={upgrade.id}
          isPlus={upgrade.isPlus}
        />
      ),
    };
  }
  if (isRolePlusUpgrade(upgrade)) {
    return {
      kind: "chip",
      node: (
        <UpgradeChip
          label={upgrade.value}
          value={rolePlusMark(upgrade.key === "rolePlusPlus")}
          showArrow={false}
        />
      ),
    };
  }
  return { label: upgrade.label, value: upgrade.value };
};

/**
 * A level's upgrades sorted into what the card draws. Keys come from
 * `levelUpgradeKeys` so a shared stat glides on a path switch; icons and
 * chips carry the key on a wrapper for the same reason.
 */
export const levelCardContent = (
  upgrades: Upgrade[],
  format: UpgradeFormatter,
): { rows: LevelCardRow[]; icons: ReactNode[]; chips: ReactNode[] } => {
  const rows: LevelCardRow[] = [];
  const icons: ReactNode[] = [];
  const chips: ReactNode[] = [];
  const keys = levelUpgradeKeys(upgrades);
  upgrades.forEach((upgrade, index) => {
    const key = keys[index];
    const view = format(upgrade);
    if (view.kind === "icon") {
      icons.push(
        <span key={key} data-row-key={key} className="inline-flex">
          {view.node}
        </span>,
      );
    } else if (view.kind === "chip") {
      chips.push(
        <span key={key} data-row-key={key} className="inline-flex">
          {view.node}
        </span>,
      );
    } else {
      rows.push({ key, label: view.label, value: view.value });
    }
  });
  return { rows, icons, chips };
};

export interface LevelsProps {
  evolution: Pick<Evolution, "upgrades" | "pathwayUpgrades">;
  /** Option index per level. Missing levels read as path 1. */
  pathwayPath?: number[];
  /** Absent means read-only: the picker shows the choice but won't take one. */
  onChange?: (pathwayPath: number[]) => void;
  /**
   * Whether a level's option can be chosen right now; drawn but inert when
   * not. Absent means every option can. Only read with `onChange`.
   */
  isOptionAvailable?: (level: number, option: number) => boolean;
  /**
   * Fires with the path that hovering an option would choose, and with
   * nothing once the pointer leaves, so the host can preview it.
   */
  onPreview?: (pathwayPath: number[] | undefined) => void;
  /** A choice is resolving: the chosen circles spin, none can be pressed. */
  loading?: boolean;
  /** Right-hand slot of the header row. */
  action?: ReactNode;
  /** "wrap" for the page body; "scroll" snap-scrolls the phone row. */
  layout?: "wrap" | "scroll";
  /**
   * How an upgrade reads on a card. Defaults to the backend's English; pages
   * pass their localised row hooks.
   */
  formatUpgrade?: UpgradeFormatter;
  showHeader?: boolean;
  /**
   * See LevelCard: the decision per level, none of the upgrades. Levels
   * without a choice are left out altogether.
   */
  choiceOnly?: boolean;
  /** The solver's pick per level; its option on each level gets a star. */
  recommendedPath?: number[];
  className?: string;
}

/**
 * The "Levels" section: one card per level of the evolution. A linear
 * evolution just lists what each level grants; a pathway evolution adds the
 * path picker and reads as "Evolution Pathway". Selecting a path only
 * changes what the caller is told; where the choice lives is its business.
 */
export const Levels = ({
  evolution,
  pathwayPath = [],
  onChange,
  isOptionAvailable,
  onPreview,
  loading = false,
  action,
  layout = "wrap",
  formatUpgrade,
  showHeader = true,
  choiceOnly = false,
  recommendedPath,
  className,
}: LevelsProps) => {
  const { t } = useTranslation("evolutions");
  // Path switches only animate on desktop; phones and tablets swap instantly.
  const animated = !useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  // Choosing a pathway is only about the levels that offer one; the rest
  // would be cards saying "no choice".
  const levels = evolutionLevels(evolution, pathwayPath).filter(
    (level) => !choiceOnly || level.hasChoice,
  );
  if (levels.length === 0) return null;
  const isPathway = isPathwayEvolution(evolution);
  const pathLabel = isPathway
    ? pathwayPathLabel(evolution, pathwayPath)
    : undefined;

  const format: UpgradeFormatter = formatUpgrade ?? defaultUpgradeView;

  const cards = levels.map((level) => (
    <LevelCard
      key={level.level}
      level={level.level + 1}
      selectedOption={level.selectedOption}
      optionCount={level.options.length}
      {...levelCardContent(level.options[level.selectedOption] ?? [], format)}
      readOnly={!onChange}
      compact={layout === "scroll"}
      onSelectOption={
        onChange &&
        ((option) =>
          onChange(withPathwayOption(pathwayPath, level.level, option)))
      }
      isOptionAvailable={
        isOptionAvailable &&
        ((option) => isOptionAvailable(level.level, option))
      }
      onHoverOption={
        onPreview &&
        ((option) =>
          onPreview(
            option === undefined
              ? undefined
              : withPathwayOption(pathwayPath, level.level, option),
          ))
      }
      loading={loading}
      animated={animated}
      recommendedOption={
        level.hasChoice ? recommendedPath?.[level.level] : undefined
      }
      choiceOnly={choiceOnly}
      className={layout === "scroll" ? "shrink-0" : undefined}
    />
  ));

  return (
    <section className={cn("flex flex-col gap-4", className)}>
      {showHeader && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
            {isPathway && (
              <div className="size-5 text-evolution-new">{evoPathwayIcon}</div>
            )}
            <span>{isPathway ? t("pathway.title") : t("sections.levels")}</span>
            {pathLabel && (
              <>
                <span
                  className="size-1 rounded-full bg-evolution-secondary"
                  aria-hidden
                />
                <div className="text-sm text-foreground-muted">{pathLabel}</div>
              </>
            )}
          </h3>
          {action}
        </div>
      )}
      {layout === "scroll" ? (
        <ScrollArea className="-mx-4" enableShadows blockClickOnScroll>
          <div className="flex items-stretch gap-3 px-4 pb-2">{cards}</div>
          <ScrollBar
            className="h-2"
            orientation="horizontal"
            scrollBarColor="bg-panel-foreground"
          />
        </ScrollArea>
      ) : (
        // A grid, not flex-wrap: `auto-rows-fr` sizes every row to the
        // tallest card, so cards on different rows stay equal too.
        <div className="grid auto-rows-fr grid-cols-[repeat(auto-fill,15rem)] gap-3">
          {cards}
        </div>
      )}
    </section>
  );
};
